import { PageContainer, PageSectionTitle } from 'components/Layout/PageContainer';
import { useModelService } from 'services/model/model.service';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from 'hooks/state/useAppStore';
import { PerformanceGapDisplay } from 'pages/Benchmarking/common/performance-gap/PerformanceGapDisplay';
import { Interpretation } from 'components/MeasureComparison';
import { FormattedMessage } from 'react-intl';
import { DataEntry, DataResult } from 'models/data';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { ExpansionOpportunities } from 'pages/Benchmarking/common/expansion-opportunities/ExpansionOpportunities';
import { useBenchmarkDataResult } from 'pages/Benchmarking/queryHooks';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { CountryMarketplacesDrilldownInfoContent } from 'pages/Benchmarking/drilldown/CountryMarketplacesDrilldownInfoContent';

export interface BenchmarkingCountryDrillDownProps {
  country: string;
}

interface DashboardData {
  data: DataResult;
}

interface ExpansionOpportunitiesData {
  data: DataResult;
  benchmark: string;
}

export function BenchmarkingCountryDrillDown({ country }: BenchmarkingCountryDrillDownProps) {
  const { setContent } = useInfoDrawer();

  useEffect(() => {
    const countryDrillDownInfo = {
      titleKey: 'benchmarking.countries_tab.drilldown.info.title',
      content: <CountryMarketplacesDrilldownInfoContent />,
    };

    setContent(countryDrillDownInfo);

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const { getMeasures, getAttribute } = useModelService();

  const { breadcrumbs, setBreadcrumbs } = useBreadcrumbStore();

  const chnMarketplace = getAttribute('chnMarketplace');
  const chnCountry = getAttribute('chnCountry');
  const [ordGmvVal, retQtyRateOD, cncQtyRate] = getMeasures(['ordGmvVal', 'retQtyRateExpOD', 'cncQtyRate']);

  const countryName = chnCountry.getValue({ chnCountry: country });

  useEffect(() => {
    if (breadcrumbs) {
      const drilldownIndex = breadcrumbs.findIndex(bc => bc.path === 'drilldown');

      if (drilldownIndex >= 0) {
        const bcSlice = breadcrumbs.slice(0, drilldownIndex);
        bcSlice.push({ path: 'countries' });
        bcSlice.push({ path: countryName });
        setBreadcrumbs(bcSlice);
      }
    }
  }, [country, breadcrumbs]);

  const [dashboardData, setDashboardData] = useState<DashboardData>();

  const [expansionOpportunitiesData, setExpansionOpportunitiesData] = useState<ExpansionOpportunitiesData>();
  const { marketplaces, benchmark } = useAppStore();

  const { allMarketplaces } = useTransitiveCacheStore();

  const allMarketplaceNames = useMemo(() => (allMarketplaces ?? []).map(m => m.name), [allMarketplaces]);

  const { data: inflightDrilldownData, isPending } = useBenchmarkDataResult(`drilldown/country/${country}`);

  useEffect(() => {
    if (inflightDrilldownData) {
      const filteredMarketplaceCountries: DataEntry[] = [];
      const expansionOpportunities: DataEntry[] = [];

      inflightDrilldownData.sets.marketplaceCountries
        .filter(mc => mc.benchmarks && mc.benchmarks[benchmark] !== null)
        .filter(mc => country === mc.attributes.chnCountry)
        .forEach(marketplaceCountry => {
          const selectedMarketplaceFound =
            marketplaces && marketplaces.length > 0
              ? marketplaces.includes(marketplaceCountry.attributes.chnMarketplace)
              : allMarketplaceNames.includes(marketplaceCountry.attributes.chnMarketplace);

          const hasOrders =
            ordGmvVal.getValue(marketplaceCountry.values) > 0 ||
            ordGmvVal.getValue(marketplaceCountry.compareValues) > 0;

          if (!selectedMarketplaceFound || !hasOrders) {
            expansionOpportunities.push(marketplaceCountry);
          } else {
            filteredMarketplaceCountries.push(marketplaceCountry);
          }
        });

      setDashboardData({
        ...inflightDrilldownData,
        data: {
          ...inflightDrilldownData,
          sets: {
            ...inflightDrilldownData?.sets,
            marketplaceCountries: filteredMarketplaceCountries,
          },
        },
      });

      setExpansionOpportunitiesData({
        ...inflightDrilldownData,
        benchmark: benchmark,
        data: {
          ...inflightDrilldownData,
          sets: {
            ...inflightDrilldownData?.sets,
            marketplaceCountries: expansionOpportunities,
          },
        },
      });
    }
  }, [inflightDrilldownData, marketplaces, allMarketplaces, country, benchmark]);

  const isLoading = isPending || !dashboardData || !allMarketplaces || !expansionOpportunitiesData;

  const dataSet = dashboardData?.data.sets.marketplaceCountries ?? [];

  return (
    <PageContainer>
      <PageSectionTitle>{countryName}</PageSectionTitle>
      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={ordGmvVal}
        groupingAttribute={chnMarketplace}
        interpretation={Interpretation.POSITIVE}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />
      <ExpansionOpportunities
        expansionOpportunities={expansionOpportunitiesData?.data.sets.marketplaceCountries ?? []}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />
      <PageSectionTitle>
        <FormattedMessage id="benchmarking.titles.returns-canellation" />
      </PageSectionTitle>
      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={retQtyRateOD}
        groupingAttribute={chnMarketplace}
        interpretation={Interpretation.NEGATIVE}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />
      <PerformanceGapDisplay
        dataSet={dataSet}
        improvementMeasure={cncQtyRate}
        groupingAttribute={chnMarketplace}
        interpretation={Interpretation.NEGATIVE}
        benchmark={expansionOpportunitiesData?.benchmark ?? ''}
        isPending={isLoading}
      />
    </PageContainer>
  );
}
